export function HowItWorks() {
  const steps = [
    {
      number: '01',
      icon: '🌍',
      title: 'Choose Your Country',
      description:
        'Browse 150+ countries and select the perfect virtual number for your target service. Numbers are instantly available — no waiting, no paperwork.',
      color: '#3B82F6',
      lightBg: '#EFF6FF',
      border: '#BFDBFE',
    },
    {
      number: '02',
      icon: '📱',
      title: 'Pick a Service',
      description:
        'Select from 300+ supported platforms including WhatsApp, Google, Instagram, Telegram, and more. Your virtual number is ready in seconds.',
      color: '#6366F1',
      lightBg: '#EEF2FF',
      border: '#C7D2FE',
    },
    {
      number: '03',
      icon: '⚡',
      title: 'Receive Your OTP',
      description:
        "The SMS verification code lands in your GetCode dashboard in under 3 seconds. Copy it, verify, and you're done — completely anonymous.",
      color: '#8B5CF6',
      lightBg: '#F5F3FF',
      border: '#DDD6FE',
    },
  ]

  return (
    <section
      id="how-it-works"
      style={{ padding: '120px 24px', maxWidth: '1200px', margin: '0 auto' }}
    >
      <div style={{ textAlign: 'center', marginBottom: '72px' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#EEF2FF',
            border: '1px solid #C7D2FE',
            borderRadius: '100px',
            padding: '6px 16px',
            marginBottom: '20px',
          }}
        >
          <span
            style={{
              fontSize: '12px',
              letterSpacing: '0.5px',
              color: '#6366F1',
              fontWeight: '600',
            }}
          >
            Simple Process
          </span>
        </div>
        <h2
          style={{
            fontSize: 'clamp(32px, 4vw, 52px)',
            fontWeight: '800',
            letterSpacing: '-1.5px',
            color: '#0F172A',
            margin: '0 0 16px',
          }}
        >
          Verified in{' '}
          <span
            style={{
              background: 'linear-gradient(135deg, #6366F1, #3B82F6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            three steps
          </span>
        </h2>
        <p
          style={{
            fontSize: '18px',
            color: '#6B7280',
            maxWidth: '480px',
            margin: '0 auto',
            lineHeight: '1.6',
          }}
        >
          No app downloads, no SIM cards, no personal data exposure.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
        }}
      >
        {steps.map((step, i) => (
          <StepCard key={i} step={step} index={i} />
        ))}
      </div>
    </section>
  )
}

function StepCard({
  step,
  index,
}: {
  step: {
    number: string
    icon: string
    title: string
    description: string
    color: string
    lightBg: string
    border: string
  }
  index: number
}) {
  return (
    <div
      style={{
        background: 'white',
        border: `1.5px solid ${step.border}`,
        borderRadius: '20px',
        padding: '36px 32px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        animationDelay: `${index * 0.1}s`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px)'
        e.currentTarget.style.boxShadow = `0 20px 48px ${step.color}20`
        e.currentTarget.style.borderColor = step.color
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'
        e.currentTarget.style.borderColor = step.border
      }}
    >
      {/* Soft tinted corner */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '140px',
          height: '140px',
          background: `radial-gradient(circle at top right, ${step.lightBg} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Big step number */}
      <div
        style={{
          fontSize: '64px',
          fontWeight: '900',
          color: `${step.color}12`,
          lineHeight: 1,
          position: 'absolute',
          top: '16px',
          right: '20px',
          letterSpacing: '-3px',
          fontFamily: 'inherit',
        }}
      >
        {step.number}
      </div>

      <div
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '16px',
          background: step.lightBg,
          border: `1.5px solid ${step.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          marginBottom: '24px',
          boxShadow: `0 4px 12px ${step.color}15`,
        }}
      >
        {step.icon}
      </div>

      <h3
        style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#0F172A',
          margin: '0 0 12px',
          letterSpacing: '-0.3px',
        }}
      >
        {step.title}
      </h3>
      <p
        style={{
          fontSize: '15px',
          color: '#6B7280',
          lineHeight: '1.7',
          margin: 0,
        }}
      >
        {step.description}
      </p>

      {/* Bottom accent */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '32px',
          right: '32px',
          height: '3px',
          background: `linear-gradient(90deg, ${step.color}, transparent)`,
          borderRadius: '3px 3px 0 0',
          opacity: 0.5,
        }}
      />
    </div>
  )
}
